// src/pages/Connector/Instagram/InstagramConnector.js

import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from "../../../../utils/axiosInstance";
import { hasPermission } from "../../../../utils/permissionUtils";
import { Box, Typography, Alert } from '@mui/material';
import ListInstagramPosts from "./ListInstagramPosts";

export default function InstagramConnector() {
    const [isConnected, setIsConnected] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isConnecting, setIsConnecting] = useState(false);
    const [error, setError] = useState('');
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [username, setUsername] = useState('');
    const location = useLocation();
    const navigate = useNavigate();

    // Permission states
    const [canView, setCanView] = useState(false);
    const [canModify, setCanModify] = useState(false);
    const [canDelete, setCanDelete] = useState(false);
    const [checkingPermissions, setCheckingPermissions] = useState(true);

    // Check permissions
    useEffect(() => {
        const checkPermissions = async () => {
            setCheckingPermissions(true);
            try {
                const viewPerm = await hasPermission('connectors', 'view');
                const modifyPerm = await hasPermission('connectors', 'add|edit');
                const deletePerm = await hasPermission('connectors', 'delete');

                setCanView(viewPerm);
                setCanModify(modifyPerm);
                setCanDelete(deletePerm);
            } catch (error) {
                console.error('Error checking permissions:', error);
                setCanView(false);
                setCanModify(false);
                setCanDelete(false);
            } finally {
                setCheckingPermissions(false);
            }
        };

        checkPermissions();
    }, []);

    const checkStatus = useCallback(async (showLoading = true) => {
        if (showLoading) setIsLoading(true);
        setError('');
        
        try {
            const response = await axiosInstance.get(`/api/instagram/status`, {
                timeout: 30000
            });
            setIsConnected(response.data.connected);
            setUsername(response.data.username || '');
            
            if (!response.data.connected) {
                const params = new URLSearchParams(location.search);
                if (params.has('error') || params.has('connected')) {
                    navigate(location.pathname, { replace: true });
                }
            }
        } catch (err) {
            console.error("Error checking Instagram status:", err);
            setError("Failed to check connection status. Please try again.");
            setIsConnected(false);
        } finally {
            setIsLoading(false);
        }
    }, [location.pathname, location.search, navigate]);

    useEffect(() => {
        document.title = 'Instagram Connector | KlonIT';
        
        if (canView) {
            const params = new URLSearchParams(location.search);
            if (params.has('connected') && params.get('connected') === 'true') {
                console.log("Successfully connected to Instagram via redirect.");
                navigate(location.pathname, { replace: true });
                checkStatus(false);
                setRefreshTrigger(prev => prev + 1);
            } else if (params.has('error')) {
                setError(`Authentication failed: ${params.get('error')}`);
                navigate(location.pathname, { replace: true });
            } else {
                checkStatus();
            }
        }
    }, [canView, checkStatus, location.pathname, location.search, navigate]);

    const handleConnect = async () => {
        setIsConnecting(true);
        setError('');

        try {
            const response = await axiosInstance.get(`/api/instagram/auth/start`);

            if (response.data && response.data.success && response.data.authorization_url) {
                window.location.href = response.data.authorization_url;
            } else {
                console.error("Backend returned error for auth/start:", response.data);
                setError(response.data.message || "Failed to initiate Instagram connection. Please try again.");
            }
        } catch (err) {
            console.error("Error calling /auth/start:", err);
            if (err.response && err.response.data && err.response.data.message) {
                setError(err.response.data.message);
            } else {
                setError("Could not connect to Instagram. Please check your network and try again.");
            }
        } finally {
            setIsConnecting(false);
        }
    };

    const handleDisconnect = async () => {
        setIsConnecting(true);
        setError('');
        
        try {
            await axiosInstance.post(`/api/instagram/disconnect`);
            setIsConnected(false);
            setUsername('');
            setRefreshTrigger(prev => prev + 1);
            setError('Successfully disconnected from Instagram.');
        } catch (err) {
            console.error("Error disconnecting Instagram:", err);
            setError(err.response?.data?.message || "Failed to disconnect. Please try again.");
        } finally {
            setIsConnecting(false);
        }
    };

    // Show loading state while checking permissions
    if (checkingPermissions) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                <Typography>Checking permissions...</Typography>
            </Box>
        );
    }

    // Show access denied if no view permission
    if (!canView) {
        return (
            <div>
                <main className="content">
                    <div className="container-fluid p-0">
                        <Alert severity="error" sx={{ mt: 6 }}>
                            <strong>Access Denied</strong>
                            <br />
                            You don't have permission to view Instagram Connector. Please contact your administrator.
                        </Alert>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div>
            <main className="content">
                <div className="container-fluid p-0">
                    <div className="mb-3">
                        <h1 className="h3 d-inline align-middle">
                            <strong>Instagram Connector</strong>
                        </h1>
                    </div>

                    {isLoading && <p>Loading configuration...</p>}
                    
                    {error && (
                        <div className={`alert ${error.includes("Success") || error.includes("successfully") ? "alert-success" : "alert-danger"} mt-3`} role="alert">
                            {error}
                        </div>
                    )}

                    {!isLoading && !isConnected && !canModify && (
                        <div className="card">
                            <div className="card-body text-center">
                                <p>You don't have permission to connect Instagram. Please contact your administrator.</p>
                            </div>
                        </div>
                    )}

                    {!isLoading && !isConnected && canModify && (
                        <div className="card">
                            <div className="card-body text-center">
                                <i className="fa fa-instagram fa-4x text-danger mb-3"></i>
                                <p>Connect your Instagram Business account to analyze post comments and train your chatbot with social media insights.</p>
                                <button 
                                    className="btn btn-lg btn-danger" 
                                    onClick={handleConnect}
                                    disabled={isConnecting}
                                >
                                    {isConnecting ? (
                                        <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Connecting...</>
                                    ) : (
                                        <><i className="fa fa-instagram me-2"></i> Connect to Instagram</>
                                    )}
                                </button>
                                <div className="mt-3">
                                    <small className="text-muted">
                                        Note: You need an Instagram Business or Creator account and also connected to a Facebook Page to use this connector.
                                    </small>
                                </div>
                            </div>
                        </div>
                    )}

                    {!isLoading && isConnected && (
                        <div className="card">
                            <div className="card-header">
                                <div className="row">
                                    <div className="col-lg-8">
                                        <h5 className="card-title mb-0">
                                            <i className="fa fa-instagram text-danger me-2"></i>
                                            Instagram Posts Management
                                        </h5>
                                        <p className="mb-0">
                                            Connected as: <strong>@{username}</strong>
                                        </p>
                                        <small className="text-muted">
                                            Analyze post comments and train your chatbot with social media insights
                                        </small>
                                    </div>
                                    <div className="col-lg-4 text-end">
                                        {canModify && (
                                            <button className="btn btn-danger" onClick={handleDisconnect} disabled={isConnecting}>
                                                {isConnecting ? (
                                                    <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Disconnecting...</>
                                                ) : (
                                                    <><i className="fa fa-unlink me-1"></i> Disconnect</>
                                                )}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="card-body">
                                <ListInstagramPosts 
                                    refreshTrigger={refreshTrigger} 
                                    permission={{
                                        canView: canView,
                                        canModify: canModify,
                                        canDelete: canDelete
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}